import React, { useCallback, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { useFormikContext } from 'formik';
import { lookupsJson, LiabilityType } from '@ourbranch/lookups';
import { isLiabilityOnlyFarmPolicy } from '@ourbranch/farm-utils';

import Section from 'core/components/section';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import InfoIcon from 'core/assets/svg/ic-info-on-card.svg';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import {
  getFarmPolicyMask,
  getMaskedLiabilityTypeOptions,
  isLiabilityCoverageHiddenByMask
} from 'common/helpers/farm-helpers';
import {
  buildDefaultFarmLiabilityCoverage,
  buildEmptyFarmLiabilityCoverage,
  getMedicalExpenseLimitOptions,
  getFarmPollutionLimitOptions
} from './helpers';
import BusinessActivities from './sections/business-activities';
import CareProvidedToOthers from './sections/care-provided-to-others';
import CustomFarming from './sections/custom-farming';
import CustomFeeding from './sections/custom-feeding';
import DomesticEmployees from './sections/domestic-employees';
import EmployersLiability from './sections/employers-liability';
import IncidentalBusinessPursuits from './sections/incidental-business-pursuits';
import Occupancy from './sections/occupancy';
import OccupiedAdditionalPropertiesExtensions from './sections/occupied-additional-properties-extensions';
import RentedAdditionalPropertiesExtensions from './sections/rented-additional-properties-extensions';
import AdditionalAssetsExtensions from './sections/additional-assets-extensions';
import useStyles from './liability-coverages-styles';

const liabilityTypeOptions = lookupsJson.farm.liabilityTypeOptions;
const farmOperationsOptions = lookupsJson.farm.farmOperations;
const eachOccurrenceLimitOptions = lookupsJson.farm.eachOccurrenceLimit;

const LiabilityCoverages = () => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { disableForm, setActiveForm } = useActiveFormState('LiabilityCoverages');

  const setLiabilityToActive = useCallback(() => {
    setActiveForm('LiabilityCoverages');
  }, [setActiveForm]);

  // #32207: resolve the product type + state route-aware. The offer store is only populated on the
  // offer route; the customer-policy route's Formik form is the segment (PolicyDetails), which
  // carries neither `policyType` nor `selectedOption`. Reading either from Formik there yielded
  // `undefined`, so the mask fell back to the permissive full-Farm (F) mask and Home/Rental
  // liability leaked farm labels + the "Primary farm operations" dropdown (whose empty value then
  // tripped the farm-worded preview error). useFarmPolicyContext reads the parent Policy from the
  // policy store on the policy route and the offer store on the offer route.
  const { farmPolicyType, state } = useFarmPolicyContext();
  const mask = getFarmPolicyMask(farmPolicyType, state);
  // #33672: Medical (M) options are brand-restricted (CFM: only $10,000; other brands: full lookup).
  // #33674: Limited Farm Pollution becomes an editable dropdown when the brand offers options (CFM:
  // $25k/$50k/$100k); it stays a read-only display otherwise.
  const medicalExpensePerPersonLimitOptions = getMedicalExpenseLimitOptions(state);
  const farmPollutionLimitOptions = getFarmPollutionLimitOptions(state);
  // #31697: Home (Z) / Rental (Y) hide "Primary farm operations" — ClarionDoor still requires
  // primary_operations (issue #31351 / error 5042), so the value is defaulted to 'O' (Other)
  // behind the scenes whenever the dropdown is hidden.
  const hideFarmOperations = Boolean(mask?.hideFarmOperations);
  // #32361: Rental (Y) removes the "Specific activity or location exclusion" toggle.
  const hideLiabilityExclusions = Boolean(mask?.hideLiabilityExclusions);

  // Issue #31288: toggling Liability ON (or OFF) resets to the empty shape —
  // no liability type, no coverage limits. The agent must then actively choose
  // Farm Personal or Farm Commercial before any coverage fields appear, and an
  // OFF→ON cycle always starts fresh (it never restores the previous type).
  // Coverage defaults are applied only once a type is picked (see
  // handleLiabilityTypeChange). This reverts the #30825 follow-up that
  // pre-selected Personal + defaults on toggle ON. Resetting to empty on toggle
  // also clears scheduled coverages (e.g. Boat liability) so they can't linger
  // across a Personal↔Commercial change.
  // The GO-imported liability schedule mod (adjustmentFactor → premium_mod_liab) has no form field, so
  // both resets below carry it over rather than silently re-rating liability unmodified.
  const importedLiabilityAdjustmentFactor = values.farmLiabilityCoverage?.adjustmentFactor;
  const withImportedLiabilityAdjustment = useCallback(
    (coverage) =>
      importedLiabilityAdjustmentFactor == null
        ? coverage
        : { ...coverage, adjustmentFactor: importedLiabilityAdjustmentFactor },
    [importedLiabilityAdjustmentFactor]
  );

  const handleIncludeFarmLiabilityChange = useCallback(() => {
    setLiabilityToActive();
    setFieldValue('farmLiabilityCoverage', withImportedLiabilityAdjustment(buildEmptyFarmLiabilityCoverage()));
  }, [setFieldValue, setLiabilityToActive, withImportedLiabilityAdjustment]);

  const handleLiabilityTypeChange = useCallback(
    (newType) => {
      setLiabilityToActive();
      const previousType = values.farmLiabilityCoverage?.type;
      if (newType && newType !== previousType) {
        const defaults = buildDefaultFarmLiabilityCoverage(newType);
        if (hideFarmOperations) {
          defaults.farmOperations = 'O';
        }
        setFieldValue('farmLiabilityCoverage', withImportedLiabilityAdjustment(defaults));
      }
    },
    [
      setFieldValue,
      setLiabilityToActive,
      values.farmLiabilityCoverage?.type,
      hideFarmOperations,
      withImportedLiabilityAdjustment
    ]
  );

  // Mirror the post-data-rules invariant `aggregate = coverageL × 2` in the
  // form so the user sees the change immediately when editing Coverage L
  // (#30825 follow-up).
  const handleCoverageLChange = useCallback(
    (newCoverageL) => {
      setLiabilityToActive();
      // Guard against `Number('') === 0` if the select is ever cleared —
      // we don't want to silently zero out the aggregate.
      const numericCoverageL = Number(newCoverageL);
      if (Number.isFinite(numericCoverageL) && numericCoverageL > 0) {
        setFieldValue('farmLiabilityCoverage.annualAggregateLimit', numericCoverageL * 2);
      }
    },
    [setFieldValue, setLiabilityToActive]
  );

  // Filter liability type options by mask (e.g., Y only allows Commercial) and apply
  // mask label overrides (Z/Y drop the farm-flavored labels — #31695). A liability-only policy may
  // be restricted further than one of the same type that insures property (#34435).
  const maskedLiabilityTypeOptions = getMaskedLiabilityTypeOptions(
    farmPolicyType,
    state,
    liabilityTypeOptions,
    isLiabilityOnlyFarmPolicy(values.farmLocations, values.includeFarmLiability)
  );

  const totalAcres = (values.farmLocations || []).reduce((acc, loc) => acc + (loc?.numberOfAcres || 0), 0);

  const typeSelected = values.includeFarmLiability && values.farmLiabilityCoverage?.type;

  // #31697 self-heal: in-flight offers may already have liability with no farmOperations
  // picked. With the dropdown hidden the agent can't fill it, so default it to 'O' here.
  const missingHiddenFarmOperations =
    hideFarmOperations && Boolean(typeSelected) && !values.farmLiabilityCoverage?.farmOperations;
  useEffect(() => {
    if (missingHiddenFarmOperations) {
      setFieldValue('farmLiabilityCoverage.farmOperations', 'O');
    }
  }, [missingHiddenFarmOperations, setFieldValue]);
  const showPersonal = typeSelected === LiabilityType.Personal;
  const showCommercial = typeSelected === LiabilityType.Commercial;

  // Helper to check if a liability sub-section is hidden by the mask
  const isHidden = (coverageName) => isLiabilityCoverageHiddenByMask(farmPolicyType, state, typeSelected, coverageName);

  return (
    <Section title="Liability" type="SubSection" id="farmLiability">
      <Grid container className={classes.liabilityContainer} alignItems="flex-start">
        <Field
          xs={12}
          type="switch"
          id="includeFarmLiability"
          name="includeFarmLiability"
          mode="dark"
          label="Liability Coverage"
          disabled={disableForm}
          onChange={handleIncludeFarmLiabilityChange}
        />
        {values.includeFarmLiability && (
          <Field
            xs={12}
            type="select"
            id="farmLiabilityCoverage.type"
            name="farmLiabilityCoverage.type"
            mode="dark"
            label="Type of liability coverage"
            options={maskedLiabilityTypeOptions}
            disabled={disableForm}
            onChange={handleLiabilityTypeChange}
          />
        )}
        {typeSelected && (
          <Grid container spacing={2}>
            {/* farm details section — renamed for Home/Rental via the policy type mask (#31697) */}
            <Grid item xs={12} id="farmLiability.farmDetails">
              <Label type="formSubTitle" className={classes.sectionDivider}>
                {mask?.liabilityDetailsLabel || 'Farm details'}
              </Label>
            </Grid>
            <Grid item xs={12} className={classes.acresRow}>
              <span className={classes.acresItem}>
                <img src={InfoIcon} alt="Notification Icon" />
                <span>Total number of acres</span>
              </span>
              <span className={classes.acresItem}>
                <span className={classes.acresLabel}>ACRES</span>
                <span className={classes.acresValue}>{totalAcres}</span>
              </span>
            </Grid>
            {/* hidden for Home/Rental — farmOperations is defaulted to 'O' instead (#31697) */}
            {!hideFarmOperations && (
              <Field
                xs={12}
                type="select"
                id="farmLiabilityCoverage.farmOperations"
                name="farmLiabilityCoverage.farmOperations"
                mode="dark"
                label="Primary farm operations (required)"
                options={farmOperationsOptions}
                disabled={disableForm}
                onChange={setLiabilityToActive}
              />
            )}

            {/* coverages section */}
            <Label id="farmLiability.coverages" type="formSubTitle" className={classes.sectionDivider}>
              Coverages
            </Label>
            <Field
              xs={4}
              type="select"
              id="farmLiabilityCoverage.coverageL"
              name="farmLiabilityCoverage.coverageL"
              mode="dark"
              label="Each Occurrence Limit (L)"
              options={eachOccurrenceLimitOptions}
              disabled={disableForm}
              onChange={handleCoverageLChange}
            />
            <Field
              xs={4}
              type="select"
              id="farmLiabilityCoverage.coverageM"
              name="farmLiabilityCoverage.coverageM"
              mode="dark"
              label="Medical expense per person limit (M)"
              options={medicalExpensePerPersonLimitOptions}
              disabled={disableForm}
              onChange={setLiabilityToActive}
            />
            {/*
              #31288: aggregate is always read-only and auto-calculated as
              2x Each Occurrence (kept in sync by handleCoverageLChange and the
              default builder), so it renders as a display value, not an
              editable select.
            */}
            <Field
              xs={4}
              type="value"
              id="farmLiabilityCoverage.annualAggregateLimit"
              name="farmLiabilityCoverage.annualAggregateLimit"
              mode="dark"
              label={showCommercial ? 'General aggregate limit' : 'Annual aggregate limit'}
              formatter={(val) => (val != null ? `$${Number(val).toLocaleString()}` : '—')}
            />
            {!isHidden('farmPollution') &&
              (farmPollutionLimitOptions ? (
                <Field
                  xs={showPersonal ? 4 : 6}
                  type="select"
                  id="farmLiabilityCoverage.farmPollutionLimit"
                  name="farmLiabilityCoverage.farmPollutionLimit"
                  mode="dark"
                  label="Limited farm pollution"
                  options={farmPollutionLimitOptions}
                  disabled={disableForm}
                  onChange={setLiabilityToActive}
                />
              ) : (
                <Field
                  xs={showPersonal ? 4 : 6}
                  type="value"
                  id="farmLiabilityCoverage.farmPollutionLimit"
                  name="farmLiabilityCoverage.farmPollutionLimit"
                  mode="dark"
                  label="Limited farm pollution"
                  formatter={(val) => (val != null ? `$${Number(val).toLocaleString()}` : val)}
                />
              ))}
            <Field
              xs={showPersonal ? 4 : 6}
              type="value"
              id="farmCoverage.fungiLiabilityLimit"
              name="farmCoverage.fungiLiabilityLimit"
              mode="dark"
              label="Limited fungi, wet or dry rot, or bacteria liability coverage"
              formatter={(val) => (val != null ? `$${Number(val).toLocaleString()}` : val)}
            />
            {showPersonal && (
              <Field
                xs={4}
                type="value"
                id="farmLiabilityCoverage.personalInjury"
                name="farmLiabilityCoverage.personalInjury"
                mode="dark"
                label="Personal injury"
                formatter={(val) => (val ? 'Included' : 'Not included')}
              />
            )}

            {/*  exclusions section — hidden on Rental (#32361)  */}
            {!hideLiabilityExclusions && (
              <>
                <Label id="farmLiability.exclusions" type="formSubTitle" className={classes.sectionDivider}>
                  Exclusions
                </Label>
                <Grid container className={classes.exclusionsContainer}>
                  <Field
                    xs={12}
                    type="switch"
                    id="farmLiabilityCoverage.hasExclusions"
                    name="farmLiabilityCoverage.hasExclusions"
                    mode="dark"
                    label="Specific activity or location exclusion"
                    disabled={disableForm}
                    onChange={setLiabilityToActive}
                  />
                  {values.farmLiabilityCoverage?.hasExclusions && (
                    <>
                      <Grid item xs={12}>
                        <Label type="infoLabelWhite">Apply exclusion to:</Label>
                      </Grid>
                      <Field
                        xs={12}
                        type="checkbox"
                        id="farmLiabilityCoverage.exclusions.excludeActivities"
                        name="farmLiabilityCoverage.exclusions.excludeActivities"
                        mode="dark"
                        label="Specific activities"
                        disabled={disableForm}
                        onChange={setLiabilityToActive}
                      />
                      {values.farmLiabilityCoverage?.exclusions?.excludeActivities && (
                        <Field
                          xs={12}
                          type="textarea"
                          id="farmLiabilityCoverage.exclusions.excludedActivitiesDescription"
                          name="farmLiabilityCoverage.exclusions.excludedActivitiesDescription"
                          label="Description of the specific activities being excluded (required)"
                          mode="dark"
                          disabled={disableForm}
                          onChange={setLiabilityToActive}
                        />
                      )}
                      <Field
                        xs={12}
                        type="checkbox"
                        id="farmLiabilityCoverage.exclusions.excludeLocations"
                        name="farmLiabilityCoverage.exclusions.excludeLocations"
                        mode="dark"
                        label="Specific locations"
                        disabled={disableForm}
                        onChange={setLiabilityToActive}
                      />
                      {values.farmLiabilityCoverage?.exclusions?.excludeLocations && (
                        <Field
                          xs={12}
                          type="textarea"
                          id="farmLiabilityCoverage.exclusions.excludedLocationsDescription"
                          name="farmLiabilityCoverage.exclusions.excludedLocationsDescription"
                          label="Description of the specific locations being excluded (required)"
                          mode="dark"
                          disabled={disableForm}
                          onChange={setLiabilityToActive}
                        />
                      )}
                    </>
                  )}
                </Grid>
              </>
            )}

            {/* personal liability sub-sections */}
            {showPersonal && (
              <>
                <BusinessActivities disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <CareProvidedToOthers disabled={disableForm} onChangeActive={setLiabilityToActive} />
                {!isHidden('customFarming') && (
                  <CustomFarming disabled={disableForm} onChangeActive={setLiabilityToActive} />
                )}
                <DomesticEmployees disabled={disableForm} onChangeActive={setLiabilityToActive} />
                {!isHidden('employersLiability') && (
                  <EmployersLiability disabled={disableForm} onChangeActive={setLiabilityToActive} />
                )}
                {!isHidden('incidentalLiability') && (
                  <IncidentalBusinessPursuits disabled={disableForm} onChangeActive={setLiabilityToActive} />
                )}
                <Occupancy disabled={disableForm} onChangeActive={setLiabilityToActive} />
              </>
            )}
            {/* commercial liability sub-sections */}
            {showCommercial && (
              <>
                {!isHidden('employersLiability') && (
                  <EmployersLiability disabled={disableForm} onChangeActive={setLiabilityToActive} />
                )}
                {!isHidden('customFarming') && (
                  <CustomFarming disabled={disableForm} onChangeActive={setLiabilityToActive} />
                )}
                {!isHidden('customFeeding') && (
                  <CustomFeeding disabled={disableForm} onChangeActive={setLiabilityToActive} />
                )}
              </>
            )}
            {/* shared sub-sections (personal + commercial) */}
            <OccupiedAdditionalPropertiesExtensions disabled={disableForm} onChangeActive={setLiabilityToActive} />
            <RentedAdditionalPropertiesExtensions disabled={disableForm} onChangeActive={setLiabilityToActive} />
            {/* "Incidental liability (ATVs, golf carts, unmanned aircraft)" — not eligible on Rental (#32042) */}
            {!isHidden('additionalAssetsExtensions') && (
              <AdditionalAssetsExtensions disabled={disableForm} onChangeActive={setLiabilityToActive} />
            )}
          </Grid>
        )}
      </Grid>
    </Section>
  );
};

export default LiabilityCoverages;
